import { Injectable } from '@angular/core';
import { ApiRequestService } from '../api-request/api-request.service';
import Ticket from 'models/ticket/ticket.model';
import ApiResponse from 'models/api-response/api-response.model';
import { LogService } from '../log/log.service';
import Log from 'models/log/log.model';
import Project from 'models/project/project.model';
import { BehaviorSubject, of } from 'rxjs';
import PaginatedList from 'models/paginated-list/paginated-list.model';
import TicketStatus from 'models/ticket-status/ticket-status.model';
import { UserService } from 'services/user/user.service';
import { ProjectService } from 'services/project/project.service';
import ProjectPermission from 'models/project-permission/project-permission.model';
import User from 'models/user/user.model';
import { HttpClient } from '@angular/common/http';
import { ConfigService } from 'services/config/config.service';
import { TokenService } from 'services/token/token-service.service';
import TicketCategory from 'models/ticket-category/ticket-category.model';

@Injectable({
  providedIn: 'root'
})
export class TicketService {
  private selectedInstances: BehaviorSubject<Ticket[]> = new BehaviorSubject([]);
  private selectedList: BehaviorSubject<PaginatedList<Ticket>> = new BehaviorSubject(new PaginatedList());

  constructor(
    public apiRequestService: ApiRequestService,
    public userService: UserService,
    public projectService: ProjectService,
    public logService: LogService,
    public httpClient: HttpClient,
    public configService: ConfigService,
    public tokenService: TokenService,
  ) { }

  public async create(instance: Ticket): Promise<Ticket> {
    return new Promise((resolve: (r: Ticket) => any, reject: (e: Error) => any) => {
      const attrs: { ticketAttribute: { id: string }, value: string[] }[] = [];
      const fts: { externalLayer: { url: string }, objectId: string }[] = [];
      for (let i = 0; i < instance.ticketAttributeValues.length; i++) {
        attrs.push({
          ticketAttribute: { id: instance.ticketAttributeValues[i].ticketAttribute.id },
          value: instance.ticketAttributeValues[i].value.map((str = '') => str.toString()),
        });
      }
      for (let i = 0; i < instance.externalFeatures.length; i++) {
        fts.push({
          objectId: instance.externalFeatures[0].objectId.toString(),
          externalLayer: {
            url: instance.externalFeatures[0].externalLayer.url
          },
        });
      }
      this.apiRequestService.post('/ticket/create', {
        project: instance.project.id,
        ticketCategory: instance.ticketCategory.id,
        ticketAttributeValues: attrs,
        position: {
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: instance.position.geometry.coordinates
          },
          properties: {}
        },
        externalFeatures: fts
      }).subscribe({
        next: async (res: ApiResponse<Ticket>) => {
          if (!res.success) {
            await this.logService.create(new Log({
              description: 'requestError: ' + JSON.stringify(res),
              event: 'serviceError',
              performer: 'TicketService.create',
              type: 'error'
            }));
            return reject(new Error('requestError'));
          }
          this.refresh();
          return resolve(new Ticket(res.data));
        },
        error: async error => {
          console.log(error);
          await this.logService.create(new Log({
            description: error,
            event: 'serviceError',
            performer: 'TicketService.create',
            type: 'error'
          }));
          return reject(new Error('requestError'));
        }
      });
    });
  }

  public async get(id: string): Promise<Ticket> {
    return new Promise((resolve: (r: Ticket) => any, reject: (e: Error) => any) => {
      this.apiRequestService.get('/ticket/get/' + id).subscribe({
        next: async (res: ApiResponse<Ticket>) => {
          if (!res.success) {
            await this.logService.create(new Log({
              description: 'requestError: ' + JSON.stringify(res),
              event: 'serviceError',
              performer: 'TicketService.get',
              type: 'error'
            }));
            return reject(new Error('requestError'));
          }
          return resolve(new Ticket(res.data));
        },
        error: async error => {
          console.log(error);
          await this.logService.create(new Log({
            description: error,
            event: 'serviceError',
            performer: 'TicketService.get',
            type: 'error'
          }));
          return reject(new Error('requestError'));
        }
      });
    });
  }

  public async getByLayerAndObjectId(projectId: string, objectId: string, layerUrl: string): Promise<Ticket[]> {
    return new Promise((resolve: (r: Ticket[]) => any, reject: (e: Error) => any) => {
      let b64Url = btoa(layerUrl);
      this.apiRequestService.get(
        '/ticket/getol/' + projectId + "/" +
        objectId + "/" +
        b64Url
      ).subscribe({
        next: async (res: ApiResponse<Ticket[]>) => {
          if (!res.success) {
            await this.logService.create(new Log({
              description: 'requestError: ' + JSON.stringify(res),
              event: 'serviceError',
              performer: 'TicketService.getByLayerAndObjectId',
              type: 'error'
            }));
            return reject(new Error('requestError'));
          }
          let resList: Ticket[] = [];
          for (let i = 0; i < res.data.length; i++) {
            resList.push(new Ticket(res.data[i]));
          }
          this.selectedList.next(new PaginatedList({ docs: resList }));
          return resolve(resList);
        },
        error: async error => {
          console.log(error);
          await this.logService.create(new Log({
            description: error,
            event: 'serviceError',
            performer: 'TicketService.getByLayerAndObjectId',
            type: 'error'
          }));
          return reject(new Error('requestError'));
        }
      });
    });
  }

  public async updateDescription(instance: Ticket): Promise<Ticket> {
    return new Promise((resolve: (r: Ticket) => any, reject: (e: Error) => any) => {
      this.apiRequestService.post('/ticket/updatedescription/', {
        ticket: instance.id,
        description: instance.description,
      }).subscribe({
        next: async (res: ApiResponse<Ticket>) => {
          if (!res.success) {
            await this.logService.create(new Log({
              description: 'requestError: ' + JSON.stringify(res),
              event: 'serviceError',
              performer: 'TicketService.updateDescription',
              type: 'error'
            }));
            return reject(new Error('requestError'));
          }
          this.refresh();
          return resolve(new Ticket(res.data));
        },
        error: async error => {
          console.log(error);
          await this.logService.create(new Log({
            description: error,
            event: 'serviceError',
            performer: 'TicketService.updateDescription',
            type: 'error'
          }));
          return reject(new Error('requestError'));
        }
      });
    });
  }

  public async updateTicketCategory(instance: Ticket): Promise<Ticket> {
    return new Promise((resolve: (r: Ticket) => any, reject: (e: Error) => any) => {
      this.apiRequestService.post('/ticket/updatecategory/', {
        ticket: instance.id,
        ticketCategory: instance.ticketCategory.id,
      }).subscribe({
        next: async (res: ApiResponse<Ticket>) => {
          if (!res.success) {
            await this.logService.create(new Log({
              description: 'requestError: ' + JSON.stringify(res),
              event: 'serviceError',
              performer: 'TicketService.updateTicketCategory',
              type: 'error'
            }));
            return reject(new Error('requestError'));
          }
          this.refresh();
          return resolve(new Ticket(res.data));
        },
        error: async error => {
          console.log(error);
          await this.logService.create(new Log({
            description: error,
            event: 'serviceError',
            performer: 'TicketService.updateTicketCategory',
            type: 'error'
          }));
          return reject(new Error('requestError'));
        }
      });
    });
  }

  public async statusList(): Promise<TicketStatus[]> {
    return new Promise((resolve: (r: TicketStatus[]) => any, reject: (e: Error) => any) => {
      this.apiRequestService.get('/ticket/statuslist/').subscribe({
        next: async (res: ApiResponse<TicketStatus[]>) => {
          if (!res.success) {
            await this.logService.create(new Log({
              description: 'requestError: ' + JSON.stringify(res),
              event: 'serviceError',
              performer: 'TicketService.statusList',
              type: 'error',
            }));
            return reject(new Error('requestError'));
          }
          const list: TicketStatus[] = [];
          for (let i = 0; i < res.data.length; i++) {
            list.push(new TicketStatus(res.data[i]));
          }
          return resolve(list);
        },
        error: async error => {
          console.log(error);
          await this.logService.create(new Log({
            description: error,
            event: 'serviceError',
            performer: 'TicketService.statusList',
            type: 'error'
          }));
          return reject(new Error('requestError'));
        }
      });
    });
  }

  public async select(id: string): Promise<Ticket> {
    const current = this.selectedInstances.getValue();
    const newList = current.filter((ticket) => ticket.id !== id);
    if (current.length === newList.length) {
      return new Promise(async (resolve: (r: Ticket) => any, reject: (e: Error) => any) => {
        const res = await this.get(id);
        this.selectedInstances.next([...current, res]);
        return resolve(res);
      });
    }
    this.selectedInstances.next(newList);
    return of(new Ticket()).toPromise();
  }

  public async list(project: Project, page: number, limit: number, user: User, status: TicketStatus, interval: [number | null, number | null], ticketCategory: TicketCategory, filter: string, district: number, searchables: any): Promise<PaginatedList<Ticket>> {
    
    return new Promise((resolve: (r: PaginatedList<Ticket>) => any, reject: (e: Error) => any) => {
      const body = {
        project: project?.id,
        searchParams: [],
      };
      if (user) body['user'] = user.username;
      if (status) body['status'] = status.name;
      if (interval[0]) body['from'] = interval[0];
      if (interval[0]) body['to'] = interval[1];
      if (ticketCategory) body['ticketCategories'] = ticketCategory.id;
      if (filter) body['filter'] = filter;
      if (district) body['district'] = district;
      Object.values(searchables).map((val: any) => { if (val.value || (val.from && val.to)) body.searchParams.push(val) });
      this.apiRequestService.post('/ticket/ultimatelist', body)
        .subscribe({
          next: async (res: ApiResponse<PaginatedList<any>>) => {
            if (!res.success) {
              await this.logService.create(new Log({
                description: 'requestError: ' + JSON.stringify(res),
                event: 'serviceError',
                performer: 'TicketService.list',
                type: 'error'
              }));
              return reject(new Error('requestError'));
            }

            return resolve(new PaginatedList({
              page: res.data.page,
              limit: res.data.limit,
              count: res.data.count,
              docs: res.data,
              user: user,
              status: status,
              interval: interval,
              previous: { project, page, limit, user, status, interval, category: ticketCategory, filter, district },
              searchables,
            }));
          },
          error: async error => {
            console.log(error);
            await this.logService.create(new Log({
              description: error,
              event: 'serviceError',
              performer: 'TicketService.list',
              type: 'error'
            }));
            return reject(new Error('requestError'));
          }
        });
    });
  }

  public exportData(project: Project, page: number, limit: number, user: User, status: TicketStatus, interval: [number | null, number | null], ticketCategory: TicketCategory, filter: string, district: number, searchables: any) {
    const body = {
      project: project?.id,
      searchParams: [],
    };
    if (user) body['user'] = user.username;
    if (status) body['status'] = status.name;
    if (interval[0]) body['from'] = interval[0];
    if (interval[0]) body['to'] = interval[1];
    if (ticketCategory) body['ticketCategories'] = ticketCategory.id;
    if (filter) body['filter'] = filter;
    if (district) body['district'] = district;
    Object.values(searchables).map((val: any) => { if (val.value || (val.from && val.to)) body.searchParams.push(val) });
    
    return this.apiRequestService.post(`/ticket/xlsxexport/${this.tokenService.getIdToken()}`, body);
  }

  public async selectPage(project: Project, page: number, limit: number, user: User, status: TicketStatus, interval: [number | null, number | null], cat: TicketCategory, filter: string, district?: number, searchables: any = {}): Promise<PaginatedList<Ticket>> {
    return new Promise(async (resolve: (r: PaginatedList<Ticket>) => any, reject: (e: Error) => any) => {
      const res = await this.list(project, page, limit, user, status, interval, cat, filter, district, searchables);
      this.selectedList.next(res);
      return resolve(res);
    });
  }

  public refresh() {
    this.selectPage(this.selectedList.value.previous.project, this.selectedList.value.page, this.selectedList.value.limit, this.selectedList.value.previous.user, this.selectedList.value.status, this.selectedList.value.previous.interval, this.selectedList.value.previous.category, this.selectedList.value.previous.filter, this.selectedList.value.previous.district, this.selectedList.value.previous.searchables);
  }

  public async update(instance: Ticket): Promise<Ticket> {
    return new Promise((resolve: (r: Ticket) => any, reject: (e: Error) => any) => {
      const attrs: { ticketAttribute: { id: string }, value: string[] }[] = [];
      for (let i = 0; i < instance.ticketAttributeValues.length; i++) {
        attrs.push({
          ticketAttribute: { id: instance.ticketAttributeValues[i].ticketAttribute.id },
          value: instance.ticketAttributeValues[i].value.map((str) => str.toString()),
        });
      }
      this.apiRequestService.post('/ticket/update/', {
        ticket: instance.id,
        address: instance.address,
        ticketAttributeValues: attrs,
        position: {
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: instance.position.geometry.coordinates
          },
          properties: {}
        }
      }).subscribe({
        next: async (res: ApiResponse<Ticket>) => {
          if (!res.success) {
            await this.logService.create(new Log({
              description: 'requestError: ' + JSON.stringify(res),
              event: 'serviceError',
              performer: 'TicketService.update',
              type: 'error'
            }));
            return reject(new Error('requestError'));
          }
          this.refresh();
          return resolve(new Ticket(res.data));
        },
        error: async error => {
          console.log(error);
          await this.logService.create(new Log({
            description: error,
            event: 'serviceError',
            performer: 'TicketService.update',
            type: 'error'
          }));
          return reject(new Error('requestError'));
        }
      });
    });
  }

  public async setStatus(instance: Ticket, status: TicketStatus): Promise<Ticket> {
    return new Promise((resolve: (r: Ticket) => any, reject: (e: Error) => any) => {
      this.apiRequestService.post('/ticket/status/', {
        ticket: instance.id,
        ticketStatus: {
          name: status.name,
        }
      }).subscribe({
        next: async (res: ApiResponse<Ticket>) => {
          if (!res.success) {
            await this.logService.create(new Log({
              description: 'requestError: ' + JSON.stringify(res),
              event: 'serviceError',
              performer: 'TicketService.setStatus',
              type: 'error'
            }));
            return reject(new Error('requestError'));
          }
          this.refresh();
          return resolve(new Ticket(res.data));
        },
        error: async error => {
          console.log(error);
          await this.logService.create(new Log({
            description: error,
            event: 'serviceError',
            performer: 'TicketService.setStatus',
            type: 'error'
          }));
          return reject(new Error('requestError'));
        }
      });
    });
  }

  /* Checks if a user has permission to update the ticket.
  * @param {User} user - User to check.
  * @param {Project} project - Project to check.
  * @returns {boolean} - True or false.
  */
  public canUpdate(ticket: Ticket): Promise<boolean> {
    return new Promise(async (resolve: (r: boolean) => any, reject: (e: Error) => any) => {
      try {
        const user = await this.userService.getCurrentUser().value;
        if (user.isSuperAdminIn(ticket.project.organizationUnit.name)) {
          return resolve(true);
        }
        const projectPermission: ProjectPermission = this.projectService.getSelectedPermissions().value;
        if (projectPermission.updateTickets > 0) {
          return resolve(true);
        }
        if (String(ticket.createdBy.username) === String(user.username)) {
          return resolve(ticket.ticketStatus.name !== "closed");
        } else {
          return resolve(false);
        }
      } catch (ex) {
        return reject(ex);
      }
    });
  }

  public async delete(instance: Ticket): Promise<boolean> {
    return new Promise((resolve: (r: boolean) => any, reject: (e: Error) => any) => {
      this.apiRequestService.delete('/ticket/' + instance.id, {}).subscribe({
        next: async (res: ApiResponse<boolean>) => {
          if (!res.success) {
            await this.logService.create(new Log({
              description: 'requestError: ' + JSON.stringify(res),
              event: 'serviceError',
              performer: 'TicketService.delete',
              type: 'error'
            }));
            return reject(new Error('requestError'));
          }
          return resolve(res.data);
        },
        error: async error => {
          console.log(error);
          await this.logService.create(new Log({
            description: error,
            event: 'serviceError',
            performer: 'TicketService.delete',
            type: 'error'
          }));
          return reject(new Error('requestError'));
        }
      });
    });
  }

  public async getImageList(id: string): Promise<string[]> {
    return new Promise((resolve: (r: string[]) => any, reject: (e: Error) => any) => {
      this.apiRequestService.get('/ticket/listimg/' + id).subscribe({
        next: async (res: ApiResponse<string[]>) => {
          if (!res.success) {
            await this.logService.create(new Log({
              description: 'requestError: ' + JSON.stringify(res),
              event: 'serviceError',
              performer: 'TicketService.getImageList',
              type: 'error'
            }));
            return reject(new Error('requestError'));
          }
          return resolve(res.data);
        },
        error: async error => {
          console.log(error);
          await this.logService.create(new Log({
            description: error,
            event: 'serviceError',
            performer: 'TicketService.getImageList',
            type: 'error'
          }));
          return reject(new Error('requestError'));
        }
      });
    });
  }

  public async uploadImage(instance: Ticket, file: File): Promise<boolean> {
    return new Promise((resolve: (r: boolean) => any, reject: (e: Error) => any) => {
      let endpoint = this.configService.getConfig().apiUrl;
      endpoint += "/ticket/img/" + instance.id;
      const formData: FormData = new FormData();
      formData.append('file', file, file.name);
      this.httpClient
        .post(endpoint, formData, {
          headers: {
            "Authorization": this.tokenService.getIdToken()
          }
        })
        .subscribe({
          next: async (res: ApiResponse<boolean>) => {
            return resolve(true);
          },
          error: async error => {
            console.log(error);
            return reject(new Error("uploadError"));
          }
        });
    })
  }

  public async deleteImage(instance: Ticket, fileName: string): Promise<boolean> {
    return new Promise((resolve: (r: boolean) => any, reject: (e: Error) => any) => {
      let endpoint = "/ticket/img/" + instance.id + "/" + fileName;
      this.apiRequestService.delete(endpoint, {}).subscribe({
        next: async (res: ApiResponse<boolean>) => {
          if (!res.success) {
            await this.logService.create(new Log({
              description: 'requestError: ' + JSON.stringify(res),
              event: 'serviceError',
              performer: 'TicketService.deleteImage',
              type: 'error'
            }));
            return reject(new Error('requestError'));
          }
          return resolve(true);
        },
        error: async error => {
          console.log(error);
          await this.logService.create(new Log({
            description: error,
            event: 'serviceError',
            performer: 'TicketService.deleteImage',
            type: 'error'
          }));
          return reject(new Error('requestError'));
        }
      });
    })
  }

  public async getSoundList(id: string): Promise<string[]> {
    return new Promise((resolve: (r: string[]) => any, reject: (e: Error) => any) => {
      this.apiRequestService.get('/ticket/listsound/' + id).subscribe({
        next: async (res: ApiResponse<string[]>) => {
          if (!res.success) {
            await this.logService.create(new Log({
              description: 'requestError: ' + JSON.stringify(res),
              event: 'serviceError',
              performer: 'TicketService.getSoundList',
              type: 'error'
            }));
            return reject(new Error('requestError'));
          }
          return resolve(res.data);
        },
        error: async error => {
          console.log(error);
          await this.logService.create(new Log({
            description: error,
            event: 'serviceError',
            performer: 'TicketService.getSoundList',
            type: 'error'
          }));
          return reject(new Error('requestError'));
        }
      });
    });
  }

  public async uploadSound(instance: Ticket, file: File): Promise<boolean> {
    return new Promise((resolve: (r: boolean) => any, reject: (e: Error) => any) => {
      let endpoint = this.configService.getConfig().apiUrl;
      endpoint += "/ticket/sound/" + instance.id;
      const formData: FormData = new FormData();
      formData.append('file', file, file.name);
      this.httpClient
        .post(endpoint, formData, {
          headers: {
            "Authorization": this.tokenService.getIdToken()
          }
        })
        .subscribe({
          next: async (res: ApiResponse<boolean>) => {
            return resolve(true);
          },
          error: async error => {
            console.log(error);
            return reject(new Error("uploadError"));
          }
        });
    })
  }

  public async deleteSound(instance: Ticket, fileName: string): Promise<boolean> {
    return new Promise((resolve: (r: boolean) => any, reject: (e: Error) => any) => {
      let endpoint = "/ticket/sound/" + instance.id + "/" + fileName;
      this.apiRequestService.delete(endpoint, {}).subscribe({
        next: async (res: ApiResponse<boolean>) => {
          if (!res.success) {
            await this.logService.create(new Log({
              description: 'requestError: ' + JSON.stringify(res),
              event: 'serviceError',
              performer: 'TicketService.deleteSound',
              type: 'error'
            }));
            return reject(new Error('requestError'));
          }
          return resolve(true);
        },
        error: async error => {
          console.log(error);
          await this.logService.create(new Log({
            description: error,
            event: 'serviceError',
            performer: 'TicketService.deleteSound',
            type: 'error'
          }));
          return reject(new Error('requestError'));
        }
      });
    })
  }

  public getSelectedInstance() {
    return this.selectedInstances;
  }

  public getSelectedList() {
    return this.selectedList;
  }
}
